import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  InputBase,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { usePlanner } from '../../contexts/PlannerContext';
// MarkdownText available but not currently used
// import { MarkdownText } from '../common/MarkdownText';
import { MessageWithMcpBadges } from '../common/MessageWithMcpBadges';
import MCPStatusBadge from '../MCPStatusBadge';
import { textScale } from '../../theme/standards';


interface AssistantConversationProps {
  onClose?: () => void;
  title?: string;
  variant?: 'drawer' | 'embedded';
  sx?: SxProps<Theme>;
  suggestions?: string[];
  borderless?: boolean;
  stickyInput?: boolean;
  disableInternalScroll?: boolean;
}

export const AssistantConversation: React.FC<AssistantConversationProps> = ({
  onClose,
  variant = 'drawer',
  sx,
  suggestions = [],
  borderless = false,
  stickyInput = false,
  disableInternalScroll = false,
}) => {
  const {
    messages,
    sendFollowUp,
    respondToConfirmation,
    loading,
    error,
    isAnyOperationInProgress,
    travelData,
    destinationInfo,
    countryDestinationInfo,
    currentSearchId,
  } = usePlanner();
  const [input, setInput] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const assistantAccent = '#10a37f';
  const surface = '#f7f7f8';
  const navigate = useNavigate();
  const [isUserNearBottom, setIsUserNearBottom] = React.useState(true);

  const submitMessage = React.useCallback(async () => {
    if (!input.trim() || loading || isAnyOperationInProgress) return;
    await sendFollowUp(input.trim());
    setInput('');
  }, [input, loading, isAnyOperationInProgress, sendFollowUp]);

  // Track scroll position to determine if user is near bottom
  const handleScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setIsUserNearBottom(distanceFromBottom < 120);
  }, []);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Auto-scroll to bottom when messages change or are being generated
  const scrollToBottom = React.useCallback((instant: boolean = false) => {
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const messagesEnd = messagesEndRef.current;
      
      if (container && messagesEnd) {
        // Use scrollIntoView for more reliable scrolling
        messagesEnd.scrollIntoView({ 
          behavior: instant ? 'auto' : 'smooth', 
          block: 'end',
          inline: 'nearest'
        });
      } else if (container) {
        // Fallback to direct scrollTop manipulation for instant scroll
        if (instant) {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }
    });
  }, []);

  React.useEffect(() => {
    // When loading (generating), always scroll to bottom immediately
    if (loading) {
      scrollToBottom(true);
      return;
    }

    // When not loading, only scroll if user is near bottom (to avoid interrupting user)
    if (isUserNearBottom) {
      scrollToBottom(false);
    }
  }, [messages, loading, isUserNearBottom, scrollToBottom]);

  // Also scroll when message text changes (for streaming updates)
  const lastMessageText = messages.length > 0 ? messages[messages.length - 1]?.text : '';
  React.useEffect(() => {
    if (loading && lastMessageText) {
      // Scroll on every text update during streaming with instant scroll
      scrollToBottom(true);
    }
  }, [lastMessageText, loading, scrollToBottom]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitMessage();
  };

  const handleSuggestionSelect = async (prompt: string) => {
    if (!prompt || loading || isAnyOperationInProgress) return;
    await sendFollowUp(prompt);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await submitMessage();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _hasItinerary = Boolean(travelData?.travel_plan?.length);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _destinationLabel =
    travelData?.destination ||
    destinationInfo?.name ||
    countryDestinationInfo?.name ||
    'Your trip';

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toISOString().slice(0, 10);
  };

  const formatTime = (iso?: string) => {
    if (!iso) return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _dateRangeLabel = travelData?.start_date && travelData?.end_date
    ? `${formatDate(travelData.start_date)} - ${formatDate(travelData.end_date)}`
    : null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _daysCount = travelData?.travel_plan?.length || travelData?.total_trip_days;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _activitiesCount =
    travelData?.activities?.length ||
    travelData?.travel_plan?.reduce(
      (total, day) => total + (day.activities?.length || 0),
      0,
    ) ||
    0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _tripPhoto =
    destinationInfo?.image_urls?.[0] ||
    destinationInfo?.image_url ||
    countryDestinationInfo?.image_urls?.[0] ||
    countryDestinationInfo?.image_url;

  const completionMessage = [...messages].reverse().find(
    (msg) => !msg.isUser && msg.text?.toLowerCase().includes('itinerary is ready'),
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _readyTime = completionMessage?.timestamp
    ? formatTime(completionMessage.timestamp)
    : formatTime();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleGoToTrip = React.useCallback(() => {
    const target = currentSearchId ? `/trip/${currentSearchId}` : '/trip';
    navigate(target);
  }, [currentSearchId, navigate]);

  const isEmbedded = variant === 'embedded';
  const hasSuggestions = suggestions.length > 0;
  const renderTripMessage = (card: {
    searchId: string;
    destination: string;
    startDate?: string | null;
    endDate?: string | null;
    days?: number | null;
    activities?: number | null;
    photoUrl?: string | null;
    timestamp?: string;
  }) => {
    const isActive = card.searchId === currentSearchId;
    const goToTrip = () => {
      const target = card.searchId ? `/trip/${card.searchId}` : '/trip';
      navigate(target);
      onClose?.();
    };

    return (
      <Paper
        elevation={3}
        role="button"
        tabIndex={0}
        onClick={goToTrip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goToTrip();
          }
        }}
        sx={{
          display: 'flex',
          gap: 1.25,
          alignItems: 'stretch',
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          cursor: 'pointer',
          flexDirection: { xs: 'column', sm: 'row' },
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            transform: 'translateY(-1px)',
            borderColor: theme.palette.primary.main,
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 120 },
            height: { xs: 110, sm: 96 },
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
            background: alpha(theme.palette.text.primary, 0.04),
          }}
        >
          {card.photoUrl ? (
            <Box
              component="img"
              src={card.photoUrl}
              alt={`${card.destination} preview`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                height: '100%',
                color: 'text.secondary',
                background: alpha(theme.palette.text.primary, 0.04),
                gap: 0.5,
              }}
            >
              <TravelExploreIcon />
              <Typography variant="caption">Trip preview</Typography>
            </Stack>
          )}
        </Box>

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {card.destination}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {card.startDate && card.endDate
              ? `${card.startDate} - ${card.endDate}`
              : 'Dates coming soon'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.25 }}>
            {card.days ? (
              <Chip
                size="small"
                label={`${card.days} day${card.days === 1 ? '' : 's'}`}
                sx={{ height: 24 }}
              />
            ) : null}
            <Chip
              size="small"
              label={`${card.activities ?? 0} activities`}
              sx={{ height: 24 }}
            />
          </Stack>
        </Stack>

        <Stack
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          sx={{ minWidth: { sm: 100 }, flexShrink: 0 }}
        >
          <Chip
            color={isActive ? 'success' : 'default'}
            size="small"
            label={isActive ? 'Active' : 'Saved'}
            sx={{ height: 24 }}
          />
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: theme.palette.primary.main }}>
            <Typography variant="body2" fontWeight={700}>
              View trip
            </Typography>
            <KeyboardArrowRightIcon fontSize="small" />
          </Stack>
        </Stack>
      </Paper>
    );
  };
  return (
    <Paper
      elevation={borderless ? 0 : (isEmbedded ? 4 : 0)}
      sx={{
        height: disableInternalScroll ? 'auto' : '100%',
        minHeight: disableInternalScroll ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        border: borderless ? 'none' : (isEmbedded ? `1px solid ${alpha('#000', 0.08)}` : undefined),
        overflow: disableInternalScroll ? 'visible' : 'hidden',
        backgroundColor: borderless ? 'transparent' : (isEmbedded ? 'transparent' : surface),
        ...sx,
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flexGrow: disableInternalScroll ? 0 : 1,
          overflowY: disableInternalScroll ? 'visible' : 'auto',
          overflowX: 'hidden',
          px: borderless ? 0 : { xs: 2, md: 4 },
        py: borderless ? 0 : 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        minHeight: disableInternalScroll ? 'auto' : 0,
        justifyContent: 'flex-start',
          backgroundColor: borderless ? theme.palette.background.paper : (isEmbedded ? theme.palette.background.paper : surface),
          // Hide scrollbar
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Opera
          },
        }}
      >
        {onClose && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton size="small" onClick={onClose}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {messages.map((message, index) => {
          const isFirst = index === 0;
          const isUser = message.isUser;

          if (message.confirmation) {
            const confirmationAccent = theme.palette.primary.main;
            const isResponded = Boolean(message.confirmation.responded);
            const responseChoice = message.confirmation.responded;

            return (
              <Fade in key={message.id}>
                <Box
                  sx={{
                    width: '100%',
                    py: borderless ? 1.5 : 1.5,
                    px: borderless ? { xs: 1.5, md: 2 } : { xs: 1.5, md: 2 },
                    mt: borderless ? (isFirst ? { xs: 1.5, sm: 3 } : 0) : 0,
                    borderBottom: borderless ? 'none' : `1px solid ${alpha('#0f172a', 0.06)}`,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: borderless ? '1000px' : '100%',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        px: borderless ? { xs: 2.5, md: 2.75 } : { xs: 2, md: 2.25 },
                        py: borderless ? 2 : 1.75,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(confirmationAccent, 0.18)}`,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '999px',
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: alpha(confirmationAccent, 0.08),
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: confirmationAccent, fontSize: 18 }} />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            letterSpacing: 0.2,
                            fontWeight: 700,
                            color: alpha(theme.palette.text.primary, 0.86),
                          }}
                        >
                          Confirmation needed
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.5, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.35 }}
                      >
                        {message.confirmation.prompt}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', lineHeight: 1.6 }}>
                        I can generate this now. Want me to proceed?
                      </Typography>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ mt: 1.75 }}
                      >
                        <Button
                          variant="contained"
                          size="medium"
                          disableElevation
                          onClick={() => respondToConfirmation(message.confirmation!.id, 'yes')}
                          disabled={loading || isResponded}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 999,
                            px: 2.5,
                            boxShadow: 'none',
                          }}
                        >
                          Yes, generate
                        </Button>
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => respondToConfirmation(message.confirmation!.id, 'no')}
                          disabled={loading || isResponded}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 999,
                            px: 2.25,
                            color: confirmationAccent,
                            borderColor: alpha(confirmationAccent, 0.35),
                            '&:hover': { backgroundColor: alpha(confirmationAccent, 0.06), borderColor: alpha(confirmationAccent, 0.5) },
                          }}
                        >
                          Not now
                        </Button>
                      </Stack>
                      {isResponded && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            color: responseChoice === 'yes' ? confirmationAccent : 'text.secondary',
                            fontWeight: 600,
                            display: 'block',
                          }}
                        >
                          {responseChoice === 'yes'
                            ? 'Great—working on your plan now.'
                            : 'Request canceled.'}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: 'text.disabled' }}
                      >
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </Fade>
            );
          }

          return (
            <Fade in key={message.id}>
              <Box
                sx={{
                  width: '100%',
                  py: borderless ? 1.5 : 1.5,
                  px: borderless ? { xs: 1.5, md: 2 } : { xs: 1.5, md: 2 },
                  mt: borderless ? (isFirst ? { xs: 1.5, sm: 3 } : 0) : 0,
                  borderBottom: borderless ? 'none' : `1px solid ${alpha('#0f172a', 0.06)}`,
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <Box
                  sx={{
                    maxWidth: borderless ? '1000px' : '100%',
                    width: '100%',
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                  }}
                >
          <Box
            sx={{
              px: borderless ? { xs: 2.5, md: 3 } : { xs: 1.5, md: 2 },
              py: borderless ? 2 : 1.25,
              borderRadius: borderless ? 3 : 2,
              backgroundColor: isUser
                ? alpha(theme.palette.primary.main, borderless ? 0.18 : 0.12)
                : 'transparent',
              border: isUser ? `1px solid ${alpha(theme.palette.primary.main, borderless ? 0.3 : 0.2)}` : 'none',
              boxShadow: isUser ? '0 12px 28px rgba(255, 56, 92, 0.18)' : 'none',
              color: isUser ? theme.palette.primary.dark : 'inherit',
              textAlign: isUser ? 'right' : 'left',
              maxWidth: '100%',
            }}
          >
            <MessageWithMcpBadges
              text={message.text}
              mcpMetadata={message.mcpMetadata}
              variant="body2"
              color="inherit"
              fontWeight={isUser ? 600 : undefined}
            />
            {message.tripCard && (
              <Box sx={{ mt: 1 }}>
                {renderTripMessage(message.tripCard)}
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: 'text.disabled' }}
            >
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Fade>
          );
        })}

        {loading && (
          <Fade in>
            <Box
              sx={{
                width: '100%',
                py: borderless ? 2.5 : 1.5,
                px: borderless ? 0 : { xs: 1.5, md: 2 },
                borderBottom: `1px solid ${alpha('#0f172a', 0.08)}`,
              }}
            >
              <Box
                sx={{
                  maxWidth: borderless ? '1000px' : '100%',
                  mx: borderless ? 'auto' : 0,
                  px: borderless ? { xs: 3, md: 4 } : 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: assistantAccent,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  TripGen
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <CircularProgress size={28} thickness={4} sx={{ color: assistantAccent }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Preparing your response...
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Fade>
        )}

        {error && (
          <Box
            sx={{
              width: '100%',
              py: borderless ? 2.5 : 1.5,
              px: borderless ? 0 : { xs: 1.5, md: 2 },
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                maxWidth: borderless ? '1000px' : '100%',
                mx: borderless ? 'auto' : 0,
                borderColor: alpha(theme.palette.error.main, 0.4),
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.dark,
                px: borderless ? { xs: 3, md: 4 } : 2,
                py: 1.5,
                borderRadius: 3,
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Paper>
          </Box>
        )}

        {/* Trip summary banner removed per request */}

        <div ref={messagesEndRef} />
      </Box>

    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        position: stickyInput ? 'fixed' : 'static',
        left: stickyInput ? { xs: 0, lg: 280 } : 'auto',
        right: stickyInput ? 0 : 'auto',
        bottom: stickyInput ? 0 : 'auto',
        zIndex: stickyInput ? 100 : 'auto',
        display: 'flex',
        justifyContent: 'center',
        px: 0,
        py: stickyInput ? { xs: 1.75, md: 2.25 } : (borderless ? { xs: 2, md: 2.5 } : { xs: 2, md: 3 }),
        borderTop: borderless ? 'none' : `1px solid ${alpha('#0f172a', 0.08)}`,
        backgroundColor: borderless ? 'transparent' : alpha(theme.palette.background.paper, stickyInput ? 0.9 : 1),
        backdropFilter: stickyInput ? 'blur(10px)' : 'none',
        transform: stickyInput ? 'none' : 'none',
        width: stickyInput ? { xs: '100%', lg: 'calc(100% - 280px)' } : 'auto',
      }}
    >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            mx: 'auto',
            px: { xs: 2, md: 4 },
          }}
        >
          {hasSuggestions && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              flexWrap={{ xs: 'nowrap', md: 'wrap' }}
              sx={{ gap: 1, mb: 1.5 }}
            >
              {suggestions.map((prompt) => (
                <Chip
                  key={prompt}
                  icon={<AutoAwesomeIcon sx={{ fontSize: textScale.body.fontSize }} />}
                  label={prompt}
                  onClick={() => handleSuggestionSelect(prompt)}
                  disabled={loading || isAnyOperationInProgress}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'flex-start', md: 'center' },
                    height: { xs: 'auto', md: 'auto' },
                    py: { xs: 1.5, md: 0.5 },
                    px: { xs: 2, md: 1 },
                    '& .MuiChip-label': {
                      whiteSpace: { xs: 'normal', md: 'normal' },
                      fontSize: textScale.support.fontSize,
                      fontWeight: 500,
                      textAlign: { xs: 'left', md: 'center' },
                      display: 'block',
                      px: { xs: 1, md: 0 },
                      overflow: 'visible',
                    },
                    '& .MuiChip-icon': {
                      ml: { xs: 0.5, md: 0.5 },
                      mr: { xs: 0.5, md: 0 },
                      fontSize: { xs: '1.1rem', md: textScale.body.fontSize },
                      flexShrink: 0,
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      mt: { xs: 0.25, md: 0 },
                    },
                  }}
                />
              ))}
            </Stack>
          )}
          <Paper
            sx={{
              borderRadius: borderless ? 6 : 4,
              border: borderless ? `1px solid ${alpha('#0f172a', 0.15)}` : `1px solid ${alpha('#0f172a', 0.12)}`,
              backgroundColor: '#fff',
              boxShadow: borderless ? '0 0 0 1px rgba(0,0,0,0.05)' : '0 30px 60px rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <MCPStatusBadge compact />
            </Box>
            <InputBase
              multiline
              minRows={1}
              maxRows={6}
              fullWidth
              placeholder="Message TripGen..."
              value={input}
              onChange={(event) => setInput(event.target.value)}
              disabled={loading}
              onKeyDown={handleKeyDown}
              sx={{
                flexGrow: 1,
                px: 1.5,
                py: 1,
                fontSize: textScale.body.fontSize,
                color: theme.palette.text.primary,
                backgroundColor: borderless ? 'transparent' : alpha(theme.palette.primary.main, 0.06),
                borderRadius: 3,
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                  caretColor: theme.palette.text.primary,
                  backgroundColor: 'transparent',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: alpha(theme.palette.text.primary, 0.5),
                },
              }}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={loading || isAnyOperationInProgress || !input.trim()}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                '&:disabled': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: alpha(theme.palette.primary.main, 0.6),
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.25),
                },
              }}
            >
              <SendRoundedIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
      </Box>
    </Paper>
  );
};
